import { ALGOLIA_INDEX_MARKERS, ALGOLIA_INDEX_CIRCLES, ALGOLIA_INDEX_COUNTIES } from '@/config/algolia.config'
import { searchMultipleQueries, search } from '@/services/algolia.service'

const SET_MARKERS_INDEX = 'SET_MARKERS_INDEX'
const SET_CIRCLES_INDEX = 'SET_CIRCLES_INDEX'
const SET_COUNTIES_INDEX = 'SET_COUNTIES_INDEX'
const SET_SOURCES = 'SET_SOURCES'
const SET_CIRCLES_IN_BOX = 'SET_CIRCLES_IN_BOX'
const UPDATE_SETTINGS_CIRCLE = 'UPDATE_SETTINGS_CIRCLE'
const UPDATE_SETTINGS_MARKER = 'UPDATE_SETTINGS_MARKER'

const state = () => {
  return {
    markersIndex: null,
    circlesIndex: null,
    countiesIndex: null,
    sources: {},
    circlesInBox: {},
    settings: {
      circle: {
        nbMaxDisplay: 1000,
        nbHits: 0,
      },
      marker: {
        nbMaxDisplay: 300,
        nbHits: 0,
      },
    },
  }
}

const mutations = {
  [UPDATE_SETTINGS_CIRCLE]: (state, setting) => {
    state.settings.circle = { ...state.settings.circle, ...setting }
  },
  [UPDATE_SETTINGS_MARKER]: (state, setting) => {
    state.settings.marker = { ...state.settings.marker, ...setting }
  },
  [SET_MARKERS_INDEX]: (state, markersIndex) => {
    state.markersIndex = markersIndex
  },
  [SET_CIRCLES_INDEX]: (state, circlesIndex) => {
    state.circlesIndex = circlesIndex
  },
  [SET_COUNTIES_INDEX]: (state, countiesIndex) => {
    state.countiesIndex = countiesIndex
  },
  [SET_SOURCES]: (state, sources) => {
    const currentKpi = Object.keys(sources)[0]
    const categoryClean = sources[currentKpi].map(category => {
      return { label: category.category_label, key: category.category, fillColor: category.fillColor }
    })
    state.sources = {
      [currentKpi]: categoryClean,
    }
  },
  [SET_CIRCLES_IN_BOX]: (state, circlesInBox) => {
    state.circlesInBox = circlesInBox
  },
}

const actions = {
  initMarkersIndex: async ({ commit, rootState }) => {
    const index = await ALGOLIA_INDEX_MARKERS(rootState.data.clientId)
    commit(SET_MARKERS_INDEX, index)
  },
  initCirclesIndex: async ({ commit, rootState }) => {
    const index = await ALGOLIA_INDEX_CIRCLES(rootState.data.clientId)
    commit(SET_CIRCLES_INDEX, index)
  },
  initCountiesIndex: async ({ commit, rootState }) => {
    const index = await ALGOLIA_INDEX_COUNTIES(rootState.data.clientId)
    commit(SET_COUNTIES_INDEX, index)
  },
  search: async ({ commit, rootState }, config) => {
    const { hits } = await search({ index: config.index, params: config.params })
    commit(config.commitName, { [rootState.refine.currentKpi]: hits }, { root: true })
  },
  searchMultipleQueries: async ({ commit, rootState }, params) => {
    const commitsSettings = params.map(param => {
      const { commitName, saveSettings, commitSettings, getAllHits, positionInRequest } = param
      delete param.commitName
      delete param.saveSettings
      delete param.commitSettings
      delete param.getAllHits
      delete param.positionInRequest
      return { commitName, saveSettings, commitSettings, getAllHits, positionInRequest }
    })
    const { results } = await searchMultipleQueries({ params })
    commitsSettings.forEach(async (setting, index) => {
      let mergeHits = []
      const { hits, ...rest } = results[index]
      const getAllHits = setting.getAllHits === true && rest.nbPages > 1
      if (setting.saveSettings === true) {
        commit(`${setting.commitSettings}`, rest, { root: true })
      }

      if (getAllHits) {
        const request = params[setting.positionInRequest - 1]
        request.params.page = 0
        const paramsAllPages = buildRequestOnEachPages(rest.nbPages, [request])
        const { results: resultsPages } = await searchMultipleQueries({ params: paramsAllPages })
        mergeHits = resultsPages.reduce((acc, curr) => acc.concat(curr.hits), [])
      }

      commit(`${setting.commitName}`, { [rootState.refine.currentKpi]: getAllHits ? mergeHits : hits }, { root: true })
    })
  },
}

const buildRequestOnEachPages = (nbPages, requestsAllPages) => {
  const lastRequest = requestsAllPages.slice(-1)[0]
  const pageIndex = lastRequest.params.page
  if (pageIndex < nbPages - 1) {
    const nextRequest = {
      indexName: lastRequest.indexName,
      params: { ...lastRequest.params, page: pageIndex + 1 },
    }
    requestsAllPages.push(nextRequest)
    buildRequestOnEachPages(nbPages, requestsAllPages)
  }
  return requestsAllPages
}

const getters = {
  sourcesWithAmount(state, getters, rootState) {
    if (!state.circlesInBox?.[rootState.refine.currentKpi] || !state.sources?.[rootState.refine.currentKpi]) return []
    const categoryAmount = state.circlesInBox[rootState.refine.currentKpi].reduce((acc, value) => {
      if (!acc[value.category]) {
        acc[value.category] = { category: value.category, amount: 0 }
      }
      acc[value.category].amount += value.amount
      return acc
    }, {})

    const sourcesAmount = state.sources[rootState.refine.currentKpi].map(source => {
      return {
        ...source,
        amount: categoryAmount[source.key]?.amount || 0,
      }
    })
    return sourcesAmount.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount)).reverse()
  },
  totalSourcesAmount(state, getters) {
    return getters.sourcesWithAmount.reduce((acc, curr) => acc + curr.amount, 0)
  },
  indexCircleOrCountiesByZoom(state, getters, rootState) {
    if (rootState.radar.currentZoom < 11) return state.countiesIndex.indexName
    return state.circlesIndex.indexName
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
